/**
 * State a new part DEFINITION, and everything that hangs off it, in one go.
 *
 * The generic `CreateEntityDialog` could technically do part of this, and that is
 * why it is being replaced: it asks for a `type_name`, which `POST /api/parts`
 * refuses outright, and it has no way to say a part is generic, what it is a kind
 * of, what it sits inside, or what is inside it. Those are the questions somebody
 * standing up a design actually has.
 *
 * The shape follows one rule: a wizard is meant to be quick and easy, and the
 * full model tree is where complex and unusual changes get applied. So
 *
 *   - there is no definition-or-usage choice. A part is ALWAYS created as a
 *     definition; a usage exists only as a child, which is what `Parent part` and
 *     `Child parts` produce. Offering the choice would be offering a mistake.
 *   - the package picker is SCOPED to the packages that already hold parts and
 *     rendered as a tree, with a new package inline. Packages are cheap; hunting
 *     through twenty-eight of them to make one part is not.
 *   - the two type questions are two controls, deliberately far apart in wording:
 *     `Specializes` is what this is a KIND OF and offers generic kinds on
 *     purpose; `Parent part` is what this SITS INSIDE. One field for both is how
 *     they were confused before, and the API now refuses `type_name` to keep them
 *     apart.
 *   - the child parts are a TABLE - a definition and a multiplicity per row -
 *     because a multiplicity is the whole reason to declare a usage rather than
 *     point at a definition twice, and a multi-select had nowhere to put one. It is
 *     the same component the pane's add-a-child dialog uses, since the back end
 *     takes the same rows in both places. Its options draw from `usable_as_type`,
 *     which is the server excluding abstract definitions, so this file never has to
 *     decide what abstract means.
 *   - a quantity gets the shared quantity control rather than a text box: it takes
 *     a NUMBER and shows the unit the field declares, so nobody types a bracket -
 *     which is what wrote `:>> dryMass = "3.5 [SI::kg]";` into a model.
 *
 * Creation is ONE call and all or nothing, so a failure leaves nothing behind and
 * the same body can simply be sent again - no step is retried on its own and
 * nothing here tries to work out how far it got.
 *
 * It ends on a report rather than closing, because the person named only the
 * definition: BEST names each usage after its definition with a lowered first
 * letter and makes it unique model-wide, so `guidance2` is an answer they could
 * not have predicted and will later search for. From there, `Add another` keeps
 * the package and clears the rest, since standing up a design means making
 * several definitions in a row.
 *
 * Everything that decides anything lives in `part-wizard-model.ts`, pure and
 * tested apart from this file - the request body above all, because "sends
 * specializes and never type_name" is a claim about a value, not about a form.
 */

import { useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import HelpTip from '../common/HelpTip'
import NewAttributesTable from './NewAttributesTable'
import QuantityGroup from '../entity/QuantityGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type {
  AttributeTypeChoice,
  CreatedPart,
  FieldSchema,
  KindSchema,
  PackageRow,
  PartRow,
} from '../../schema'
import { groupByAttribute } from '../../schema'
import { useCanWrite, useSchema } from '../../state/AppData'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import FieldControl from '../entity/FieldControl'
import ElementPicker from '../common/ElementPicker'
import type { PickerOption } from '../common/ElementPicker'
import ChildPartsTable from './ChildPartsTable'
import {
  INSTANCE_HELP,
  ABSTRACT_HAS_NO_PARENT,
  attributeRowsProblem,
  ABSTRACT_HELP,
  CHILDREN_HELP,
  NEW_PACKAGE,
  PACKAGE_HELP,
  PARENT_HELP,
  SPECIALIZES_HELP,
  buildCreateBody,
  describeCreated,
  emptyForm,
  localRefusal,
  newPackageRefusal,
  packageChoices,
  packageOption,
  partOption,
  definitionChoices,
  readiness,
  summarizeCreated,
  typeChoices,
  wizardFields,
} from './part-wizard-model'
import type { WizardForm } from './part-wizard-model'

/** Enough rows to be the whole model; the listing is paged and this is a picker. */
const EVERYTHING = 2000

/** Not a package: the choice that opens the new-package fields. */
const NEW_PACKAGE_OPTION: PickerOption = {
  value: NEW_PACKAGE,
  label: 'New package…',
  chosen: 'New package…',
}

export default function PartWizard({
  kind,
  pkg = '',
  onClose,
  onCreated,
}: {
  kind: KindSchema
  /**
   * The parent package to start on - the model tree's New on a package row
   * (issue #53). Pinned, so it is offered even where the picker's scope (the
   * packages already holding parts) would leave it out; still changeable.
   */
  pkg?: string
  onClose: () => void
  /** Called with the last definition's ID once the wizard is finished with. */
  onCreated: (identifier: string) => void
}) {
  const permission = useCanWrite()
  const schema = useSchema()
  const toasts = useToasts()

  const [packages, setPackages] = useState<PackageRow[]>([])
  const [packageWarning, setPackageWarning] = useState('')
  const [baseTypes, setBaseTypes] = useState<string[]>([])
  const [pinned, setPinned] = useState<string[]>(() => (pkg ? [pkg] : []))
  // Two SEPARATE requests, and the difference is the point: `definitions` is
  // /api/parts unfiltered, which includes generic kinds, and feeds Specializes
  // and Parent part; `usableAsType` is the server's own answer to "may this be
  // the declared type of a usage", which excludes them, and feeds Child parts.
  const [definitions, setDefinitions] = useState<PartRow[]>([])
  const [usableAsType, setUsableAsType] = useState<PartRow[]>([])

  const [form, setForm] = useState<WizardForm>(() => emptyForm(pkg))
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Everything created while this dialog has been open, newest last: 'Add
  // another' is the normal way to use it, and a report that replaced itself
  // would lose the names of the usages from the run before.
  const [made, setMade] = useState<CreatedPart[]>([])

  const [newParent, setNewParent] = useState('')
  const [newName, setNewName] = useState('')
  const [newShortName, setNewShortName] = useState('')
  const [makingPackage, setMakingPackage] = useState(false)

  useEffect(() => {
    let live = true
    // SystemDesign and under (issue #114); packageChoices then scopes that to
    // the packages already holding parts.
    api
      .packagesFor('design_part')
      .then((answer) => {
        if (!live) return
        setPackages(answer.items)
        setPackageWarning(answer.warning)
      })
      .catch(() => live && setPackages([]))
    api
      .parts({ limit: EVERYTHING })
      .then((page) => {
        if (!live) return
        setDefinitions(page.items)
        // The library's part defs ride along on the same request: they are not
        // rows, so they come as a list of names beside them.
        setBaseTypes(page.base_types ?? [])
      })
      .catch(() => {
        if (!live) return
        setDefinitions([])
        setBaseTypes([])
      })
    api
      .parts({ usable_as_type: true, limit: EVERYTHING })
      .then((page) => live && setUsableAsType(page.items))
      .catch(() => live && setUsableAsType([]))
    return () => {
      live = false
    }
  }, [])

  const choices = useMemo(
    () => packageChoices(packages, definitions, pinned),
    [packages, definitions, pinned],
  )
  const specializesOptions = useMemo(() => definitionChoices(definitions), [definitions])
  const childOptions = useMemo(() => typeChoices(usableAsType), [usableAsType])
  /**
   * The attributes the chosen base type can carry, from the model.
   *
   * Null until a type is chosen and until the answer arrives, which keeps every
   * field - a form briefly too wide is better than one that hides the attribute
   * somebody opened it to state.
   *
   * Refetched on every change of `specializes`, because that IS the question:
   * a thruster and a bracket are both parts and share almost nothing.
   */
  const [applicable, setApplicable] = useState<string[] | null>(null)

  useEffect(() => {
    const target = form.specializes
    if (!target) {
      setApplicable(null)
      return undefined
    }
    let live = true
    api
      .partAttributes(target)
      .then((answer) => live && setApplicable(answer))
      // A type whose attributes cannot be read is not a reason to refuse the
      // whole form: fall back to offering everything, which is what it did
      // before this existed.
      .catch(() => live && setApplicable(null))
    return () => {
      live = false
    }
  }, [form.specializes])

  /**
   * The quantity kinds a NEW attribute may be declared as.
   *
   * Fetched once and not per keystroke: the list is a property of the install,
   * not of the form. An empty list on failure is the honest fallback - the
   * picker offers nothing rather than offering a type the server would refuse,
   * and the rest of the wizard still works.
   */
  const [attributeTypes, setAttributeTypes] = useState<AttributeTypeChoice[]>([])

  useEffect(() => {
    let live = true
    api
      .attributeTypes()
      .then((answer) => live && setAttributeTypes(answer))
      .catch(() => live && setAttributeTypes([]))
    return () => {
      live = false
    }
  }, [])

  const attributeProblem = useMemo(
    () => attributeRowsProblem(form.newAttributes),
    [form.newAttributes],
  )

  const fields = useMemo(() => wizardFields(kind, applicable), [kind, applicable])

  // The attributes go in a TABLE and everything else keeps its own control: a
  // checkbox in a value column is a row that does not read like the rows around
  // it, and there are only ever a couple of those.
  // Grouped, so a structure is one row rather than one per leaf: a centre of
  // gravity is three fields and one fact.
  const quantities = useMemo(
    () => groupByAttribute(fields.filter((field) => field.kind === 'quantity')),
    [fields],
  )

  /**
   * The library's base types, offered beside the program's own definitions.
   *
   * `definitionChoices` is built from the parts LISTING, which is a program's
   * own parts - so `EchelonPart` and its subtypes, where every quantity in the
   * profile is declared, were not on offer at all. They are appended rather than
   * merged in: a program's own definitions are what somebody usually wants, and
   * the bases sit under them.
   */
  const withBases = useMemo(() => {
    const known = new Set(specializesOptions.map((choice) => choice.id))
    const bases = baseTypes
      .filter((name) => !known.has(name))
      // Shaped as a PartChoice, with the name standing in for both keys: a
      // library def has no projection row and so no ID of its own, and the
      // server resolves a base type by name.
      .map((name) => ({ id: name, name, qualified_name: name, isGeneric: true }))
    return [...specializesOptions, ...bases]
  }, [specializesOptions, baseTypes])
  const others = useMemo(() => fields.filter((field) => field.kind !== 'quantity'), [fields])

  /** One scalar attribute: its name and '?' on the left, its control on the right. */
  function renderQuantity(field: FieldSchema) {
    return (
      <TableRow key={field.name}>
        <TableCell sx={{ width: '40%', border: 0, pl: 0, verticalAlign: 'middle' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2">{field.label}</Typography>
            {field.help && (
              <HelpTip title={field.help} label={`What ${field.label} means`} />
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ border: 0, pr: 0 }}>
          {/* The SHARED control, which takes a number and shows the field's own
              unit - `dryMass : ISQ::MassValue`, so `SI::kg` is a property of the
              field rather than a choice the typist makes. A plain text box here
              is what wrote `:>> dryMass = "3.5 [SI::kg]";` into the model. */}
          <FieldControl
            schema={schema}
            kind={kind}
            // Label and help live in the cell beside it - the first as text, the
            // second behind the '?'. The input keeps its accessible NAME through
            // ariaLabel: blanking the label without it leaves a box a screen
            // reader calls "edit text".
            field={{ ...field, label: '', help: '' }}
            ariaLabel={field.label}
            value={form.extras[field.name]}
            onChange={(next) => change({ extras: { ...form.extras, [field.name]: next } })}
            disabled={!permission.allowed}
            error={fieldErrors[field.name]}
          />
        </TableCell>
      </TableRow>
    )
  }

  // Two different things, and both are wanted. `conflict` is the pure guard over
  // the pair the API refuses, which the form cannot now assemble because ticking
  // the flag clears the parent. `parentReason` is what the person is told, and it
  // has to be said the moment the flag goes on rather than once a parent has been
  // chosen - by then the control is already disabled and would explain nothing.
  const conflict = localRefusal(form)
  const parentReason = form.abstract ? ABSTRACT_HAS_NO_PARENT : conflict
  const refusal = readiness(form, permission)
  const latest = made.length ? made[made.length - 1] : null
  const report = latest ? describeCreated(latest) : null

  const change = (patch: Partial<WizardForm>) => setForm((current) => ({ ...current, ...patch }))

  const submit = async () => {
    setBusy(true)
    setFailure('')
    setFieldErrors({})
    try {
      const created = await api.createPart(buildCreateBody(form, fields))
      toasts.succeeded(summarizeCreated(created))
      setMade((current) => [...current, created])
    } catch (error) {
      const failed = error as ApiError
      setFieldErrors(failed.fields ?? {})
      // The message is shown even when there are field complaints: the API's own
      // refusals - a generic kind with a parent, an abstract child, a destination
      // that is an item def - arrive as one sentence and no field at all, and
      // dropping it would leave a form that refused silently.
      setFailure(failed.message)
    } finally {
      setBusy(false)
    }
  }

  const addPackage = async () => {
    setMakingPackage(true)
    setFailure('')
    try {
      const created = await api.createPackage({
        parent: newParent,
        name: newName.trim(),
        short_name: newShortName.trim() || undefined,
      })
      setPackages((current) => [
        ...current,
        {
          qualified_name: created.qualified_name,
          name: created.name,
          depth: created.qualified_name.split('::').length - 1,
        },
      ])
      // Pinned, or the scope would drop it again the moment it appeared: a brand
      // new package holds no part definitions and is nobody's ancestor.
      setPinned((current) => [...current, created.qualified_name])
      change({ package: created.qualified_name })
      setNewName('')
      setNewShortName('')
      toasts.succeeded(`${created.qualified_name} created`)
    } catch (error) {
      setFailure((error as ApiError).message)
    } finally {
      setMakingPackage(false)
    }
  }

  const finish = () => {
    if (latest) onCreated(latest.identifier)
    else onClose()
  }

  const another = () => {
    // The package is kept and everything else cleared: several definitions in
    // one subsystem is the ordinary way a design gets stood up.
    setForm(emptyForm(form.package))
    setFailure('')
    setFieldErrors({})
    setMade([])
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New part definition</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {!permission.allowed && <Alert severity="info">{permission.reason}</Alert>}

          {failure && <Alert severity="error">{failure}</Alert>}

          {report && (
            <Alert severity="success" aria-label="What was created">
              <AlertTitle>Created</AlertTitle>
              <Stack spacing={0.5}>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: MONOSPACE }}>
                    {report.definition.name}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    {report.definition.note}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', fontFamily: MONOSPACE }}
                  >
                    {report.definition.qualified_name}
                  </Typography>
                </Box>
                {report.usages.map((usage) => (
                  <Box key={usage.qualified_name}>
                    <Typography variant="body2" sx={{ fontFamily: MONOSPACE }}>
                      {usage.name}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {usage.note}
                    </Typography>
                  </Box>
                ))}
                {report.caption && (
                  <Typography variant="caption" sx={{ display: 'block', pt: 1 }}>
                    {report.caption}
                  </Typography>
                )}
              </Stack>
            </Alert>
          )}

          {!report && (
            <>
              <ElementPicker
                options={[...choices.map(packageOption), NEW_PACKAGE_OPTION]}
                value={form.package || null}
                disabled={!permission.allowed}
                onChange={(chosen) => {
                  change({ package: chosen })
                  if (chosen === NEW_PACKAGE && !newParent) {
                    // Default the new package's parent to whatever was selected
                    // before, or to the shallowest package in scope.
                    setNewParent(form.package || choices[0]?.qualified_name || '')
                  }
                }}
                label="Parent package"
                required
                monospace
                error={Boolean(fieldErrors.package)}
                helperText={fieldErrors.package || packageWarning || PACKAGE_HELP}
              />

              {form.package === NEW_PACKAGE && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle2">New package</Typography>
                    <ElementPicker
                      options={choices.map(packageOption)}
                      value={newParent || null}
                      onChange={setNewParent}
                      label="Goes in"
                      required
                      monospace
                      helperText="A package needs a parent; BEST cannot yet decide which file would own a root one."
                    />
                    <TextField
                      label="Package name"
                      required
                      value={newName}
                      onChange={(event) => setNewName(event.target.value)}
                      size="small"
                    />
                    <TextField
                      label="Package ID"
                      value={newShortName}
                      onChange={(event) => setNewShortName(event.target.value)}
                      size="small"
                      helperText="Optional short name"
                      slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
                    />
                    <Tooltip title={newPackageRefusal(newParent, newName)} describeChild>
                      <span>
                        <Button
                          variant="outlined"
                          onClick={() => void addPackage()}
                          disabled={
                            Boolean(newPackageRefusal(newParent, newName)) ||
                            makingPackage ||
                            !permission.allowed
                          }
                        >
                          {makingPackage ? 'Creating…' : 'Create package'}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </Paper>
              )}

              <TextField
                label="ID"
                required
                value={form.identifier}
                disabled={!permission.allowed}
                onChange={(event) => change({ identifier: event.target.value })}
                error={Boolean(fieldErrors.identifier)}
                helperText={fieldErrors.identifier || 'The short name, e.g. SEK'}
                size="small"
                slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
              />

              <TextField
                label="Name"
                value={form.name}
                disabled={!permission.allowed}
                onChange={(event) => change({ name: event.target.value })}
                error={Boolean(fieldErrors.name)}
                helperText={fieldErrors.name || 'The display name; defaults to the ID'}
                size="small"
              />

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.abstract}
                      disabled={!permission.allowed}
                      // Ticking it CLEARS the parent part, so the pair the API
                      // refuses can never be assembled here - rather than
                      // leaving a chosen parent in a disabled control, where the
                      // only way out would be to untick the flag again.
                      onChange={(event) =>
                        change({
                          abstract: event.target.checked,
                          parentPart: event.target.checked ? '' : form.parentPart,
                          instance: event.target.checked ? false : form.instance,
                        })
                      }
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Generic kind
                      <HelpTip title={ABSTRACT_HELP} label="What a generic kind is" />
                    </Box>
                  }
                />

                {/* MAKE THE ROOT, TOO. A definition on its own is a declaration;
                    a design needs an uncontained USAGE of one, and there was no
                    way to ask for it - the root had to be hand-written and
                    imported. Beside 'Generic kind' because the two contradict:
                    a kind with no instances cannot be instanced. */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.instance}
                      // Ticking 'Generic kind' unticks this rather than leaving
                      // the pair the API refuses assembled in the form, which is
                      // how the parent-part control already behaves.
                      disabled={!permission.allowed || form.abstract}
                      onChange={(event) => change({ instance: event.target.checked })}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Also create an instance
                      <HelpTip title={INSTANCE_HELP} label="What an instance is" />
                    </Box>
                  }
                />
              </Box>

              <Divider />

              <ElementPicker
                options={withBases.map(partOption)}
                value={form.specializes || null}
                disabled={!permission.allowed}
                onChange={(next) => change({ specializes: next })}
                label="Specializes"
                error={Boolean(fieldErrors.specializes)}
                helperText={fieldErrors.specializes || SPECIALIZES_HELP}
              />

              <Tooltip title={parentReason} describeChild>
                <span>
                  <ElementPicker
                    options={specializesOptions.map(partOption)}
                    value={form.parentPart || null}
                    // Disabled rather than hidden, with the reason on it: a
                    // control that vanished when 'Generic kind' was ticked would
                    // read as a broken form rather than as a rule.
                    disabled={!permission.allowed || form.abstract}
                    onChange={(next) => change({ parentPart: next })}
                    label="Parent part"
                    fullWidth
                    error={Boolean(fieldErrors.parent_part)}
                    helperText={fieldErrors.parent_part || parentReason || PARENT_HELP}
                  />
                </span>
              </Tooltip>

              {/* A TABLE, not a multi-select: a row is a definition plus a
                  multiplicity, which is the row `child_parts` takes and the same
                  row the pane's add-a-child dialog sends to
                  POST /api/parts/{id}/children. One component, one row shape. */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Child parts
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {CHILDREN_HELP}
                </Typography>
                <ChildPartsTable
                  options={childOptions}
                  rows={form.childParts}
                  onChange={(rows) => change({ childParts: rows })}
                  disabled={!permission.allowed}
                  error={fieldErrors.child_parts ?? ''}
                />
              </Box>

              {fields.length > 0 && <Divider />}

              {/* THE ATTRIBUTES, AS A TABLE.
                  One row per quantity, with its description behind a '?' rather
                  than printed under every box: thirty labelled inputs each with
                  a sentence of helper text is a dialog nobody can see the bottom
                  of, and the sentences are worth reading once rather than every
                  time. Which rows exist is the model's answer for the chosen
                  type - a bracket is no longer asked for its chamber pressure. */}
              {quantities.length > 0 && (
                <Box>
                  <Stack direction="row" sx={{ alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="subtitle2">Attributes</Typography>
                    <HelpTip
                      label="Which attributes are offered"
                      title={
                        form.specializes
                          ? 'The attributes this type can carry, read from the model: an ' +
                            'attribute belongs to the part definition that declares it, so a ' +
                            'sensor is offered a field of view and a bracket is not. Choose a ' +
                            'different base type to see a different set.'
                          : 'Every attribute the profile declares. Choose what this ' +
                            'specializes and the list narrows to the ones that type can ' +
                            'actually carry.'
                      }
                    />
                  </Stack>
                  <Table size="small">
                    <TableBody>
                      {quantities.map((group) =>
                        // A STRUCTURE SPANS THE ROW. Six tensor terms in the
                        // value column would be six rows whose left cells all
                        // read 'Inertia'.
                        group.isStructured ? (
                          <TableRow key={group.key}>
                            <TableCell colSpan={2} sx={{ border: 0, px: 0, pb: 1.5 }}>
                              <QuantityGroup
                                schema={schema}
                                kind={kind}
                                group={group}
                                valueOf={(field) => form.extras[field.name]}
                                onChange={(field, next) =>
                                  change({ extras: { ...form.extras, [field.name]: next } })
                                }
                                disabled={!permission.allowed}
                                fieldErrors={fieldErrors}
                              />
                            </TableCell>
                          </TableRow>
                        ) : (
                          renderQuantity(group.fields[0])
                        ),
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )}


              {/* DECLARING an attribute, as opposed to stating a value for one.
                  Sits directly under the values table because the two answer
                  consecutive questions: what this part can state, and what to do
                  when the thing you want to state is not there. */}
              <NewAttributesTable
                rows={form.newAttributes}
                choices={attributeTypes}
                onChange={(rows) => change({ newAttributes: rows })}
                disabled={!permission.allowed}
                error={attributeProblem}
              />

              {/* From the registry, not from a list written here - a field added
                  to the part kind appears without this file being touched. */}
              {others.map((field: FieldSchema) =>
                // A quantity goes through the SHARED control, which takes a
                // number and shows the field's own unit - `dryMass :
                // ISQ::MassValue`, so `SI::kg` is a property of the field rather
                // than a choice the typist makes. A text box here is what wrote
                // `:>> dryMass = "3.5 [SI::kg]";` into the model.
                // No quantity branch: those are the table above, and `others`
                // is by construction everything that is not one.
                field.kind === 'flag' ? (
                  <FormControlLabel
                    key={field.name}
                    control={
                      <Checkbox
                        checked={form.extras[field.name] === true}
                        disabled={!permission.allowed}
                        onChange={(event) =>
                          change({
                            extras: { ...form.extras, [field.name]: event.target.checked },
                          })
                        }
                      />
                    }
                    label={field.label}
                  />
                ) : (
                  <TextField
                    key={field.name}
                    label={field.label}
                    value={String(form.extras[field.name] ?? '')}
                    disabled={!permission.allowed}
                    onChange={(event) =>
                      change({ extras: { ...form.extras, [field.name]: event.target.value } })
                    }
                    multiline={field.kind === 'doc'}
                    minRows={field.kind === 'doc' ? 2 : undefined}
                    error={Boolean(fieldErrors[field.name])}
                    helperText={fieldErrors[field.name] || field.help}
                    size="small"
                  />
                ),
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {report ? (
          <>
            <Button onClick={another}>Add another</Button>
            <Button variant="contained" onClick={finish}>
              Done
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Tooltip title={refusal} describeChild>
              <span>
                <Button
                  variant="contained"
                  onClick={() => void submit()}
                  disabled={Boolean(refusal) || busy}
                  startIcon={busy ? <CircularProgress size={16} /> : undefined}
                >
                  {busy ? 'Creating…' : 'Create'}
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
